/**
 * The body of a `DataTable` whose rows have not arrived: skeleton rows in the
 * table's own columns, so the layout does not move when the data lands.
 *
 * @module components/ui/DataTableSkeleton
 */

import React from "react";

export interface DataTableSkeletonProps {
  rows: number;
  /** Cells per row, which is the table's own column count. */
  columns: number;
  /** The cell padding classes the table uses at its density. */
  cell: string;
  /** Names what is loading, for the screen reader. */
  label: string;
}

/** @internal Drawn by `DataTable` while `loading`. */
export function DataTableSkeleton({
  rows,
  columns,
  cell,
  label,
}: DataTableSkeletonProps): React.ReactElement {
  return (
    <tbody>
      {Array.from({ length: rows }, (_, rowIndex) => (
        // Placeholder rows have no identity beyond their position.
        <tr key={`loading-${String(rowIndex)}`}>
          {Array.from({ length: columns }, (__, cellIndex) => (
            <td
              key={`loading-${String(rowIndex)}-${String(cellIndex)}`}
              className={`border-b border-base-300 ${cell}`}
            >
              {rowIndex === 0 && cellIndex === 0 && label ? (
                <span role="status" aria-label={label} className="sr-only">
                  {label}
                </span>
              ) : null}
              <div aria-hidden="true" className="skeleton h-4 w-full" />
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  );
}
